import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const CampaignsPage = () => {
  const navigate = useNavigate();

  // State
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch campaigns
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // Fetch all campaigns across all brands
      const response = await fetch(`/api/campaigns?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setCampaigns(data.campaigns || []);
        setTotalCampaigns(data.total || 0);
      } else {
        setCampaigns([]);
        setTotalCampaigns(0);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns. Please try again.');
      setLoading(false);
      toast.error('Failed to load campaigns');
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, sortBy, sortOrder]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleViewCampaign = (campaignId) => {
    navigate(`/campaigns/${campaignId}/details`);
  };

  const handleEditCampaign = (campaignId) => {
    navigate(`/campaigns/${campaignId}/edit`);
  };

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'draft':
        return 'default';
      case 'paused':
        return 'warning';
      case 'ended':
        return 'error';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  // Calculate overall progress for a campaign
  const calculateProgress = (campaign) => {
    if (!campaign.tiers || campaign.tiers.length === 0) return 0;

    const totalReservations = campaign.tiers.reduce((sum, tier) => sum + tier.currentReservations, 0);
    const totalTarget = campaign.tiers.reduce((sum, tier) => sum + tier.reservationTarget, 0);

    return totalTarget > 0 ? Math.round((totalReservations / totalTarget) * 100) : 0;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Campaigns
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage campaigns and monitor performance across upcoming drops
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCampaigns} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/campaigns/new')}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="ended">Ended</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </TextField>

          <TextField
            select
            label="Sort By"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="createdAt">Created Date</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="timeline.launchDate">Launch Date</MenuItem>
          </TextField>

          <TextField
            select
            label="Order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="desc">Newest First</MenuItem>
            <MenuItem value="asc">Oldest First</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Campaigns Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ '& .MuiTableCell-root': { color: 'common.white' } }}>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Launch Date</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell align="right">Reservations</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Conversion</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No campaigns found. Create your first campaign to get started!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow
                    key={campaign._id}
                    hover
                    sx={{ '&:hover': { cursor: 'pointer' } }}
                    onClick={() => handleViewCampaign(campaign._id)}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{campaign.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {campaign.tiers?.length || 0} tier{campaign.tiers?.length !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.status}
                        color={getStatusColor(campaign.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {campaign.timeline?.launchDate
                        ? formatDate(campaign.timeline.launchDate)
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 60,
                            height: 8,
                            bgcolor: 'grey.200',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              width: `${calculateProgress(campaign)}%`,
                              height: '100%',
                              bgcolor: 'primary.main',
                              transition: 'width 0.3s'
                            }}
                          />
                        </Box>
                        <Typography variant="caption">
                          {calculateProgress(campaign)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {campaign.analytics?.totalReservations || 0}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(campaign.analytics?.totalRevenue || 0)}
                    </TableCell>
                    <TableCell align="right">
                      {(campaign.analytics?.conversionRate || 0).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewCampaign(campaign._id)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditCampaign(campaign._id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Analytics">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/campaigns/${campaign._id}/analytics`)}
                        >
                          <TrendingUpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCampaigns}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </Container>
  );
};

export default CampaignsPage;
